import Pusher from 'pusher-js';
import Channel from 'pusher-js/types/src/core/channels/channel';
import Message from '@/models/MessageModel';
import { Options } from 'pusher-js/types/src/core/options';
import {
	CallbackParam,
	NewMessagePayload,
	StateChangePayload,
	PayloadFromServer
} from './interfaces';
import TriggerMessagePayload from '@/models/TriggerMessagePayloadModel';

type TransferMessages = (m: PayloadFromServer) => void;
type TransferStatus = (s: string) => void;

// Сингл тон для взаимодействия с пушером
export default class PusherService {
	private static instance: PusherService;

	private Pusher: Pusher;
	private channelStream!: Channel;
	private conversationId!: string;
	public transferMessages!: TransferMessages;
	public transferStatus!: TransferStatus;
	public state!: string;

	constructor(
		conversationId: string,
		transferMessages: TransferMessages,
		transferStatus: TransferStatus,
		opt: Options
	) {
		if (PusherService.instance) {
			throw new Error('Error - use PusherService.getInstance()');
		}
		const pusherEnv = process.env.VUE_APP_PUSHER! || 'pusher';
		const appKey = process.env.VUE_APP_PUSHER_KEY;
		const cluster = process.env.VUE_APP_PUSHER_CLUSTER;
		const proxy = process.env.VUE_APP_API_URL;
		const host = process.env.VUE_APP_PUSHER_HOST!;
		const port = +process.env.VUE_APP_PUSHER_PORT!;
		const tls = !!process.env.VUE_APP_PUSHER_TLS! || false;

		if (pusherEnv == 'pusher') {
			opt = {
				cluster,
				authEndpoint: proxy ? `${proxy}/pusher/auth/private` : '/pusher/auth/private'
			};
		} else {
			opt = {
				wsHost: host,
				wsPort: port,
				wssPort: port,
				forceTLS: tls,
				disableStats: true,
				enabledTransports: ['ws', 'wss'],
				authEndpoint: proxy ? `${proxy}/pusher/auth/private` : '/pusher/auth/private'
			};
		}

		this.Pusher = new Pusher(appKey, opt);

		// И сразу же подписываемся на диалог
		this.subscribeToConversation(conversationId, transferMessages, transferStatus);
	}

	// transferMessages - Функция которая прокидывает новое сообщение в состояние внутри компонента
	// transferStatus - Функция которая прокидывает статус внутрь компонента
	static getInstance(
		conversationId: string,
		transferMessages: TransferMessages,
		transferStatus: TransferStatus,
		opt: Options
	): PusherService {
		PusherService.instance =
			PusherService.instance ||
			new PusherService(conversationId, transferMessages, transferStatus, opt);
		return PusherService.instance;
	}

	private subscribeToConversation(
		conversationId: string,
		transferMessages: TransferMessages,
		transferStatus: TransferStatus
	) {
		if (this.isSubscribed) return;

		this.transferMessages = transferMessages;
		this.transferStatus = transferStatus;
		this.conversationId = conversationId;

		this.channelStream = this.Pusher.subscribe(
			`private-Webbot.Conversation.${conversationId}`
		);
		this.channelStream.bind(
			'App\\Events\\Webbot\\MessageWasSended',
			(p: CallbackParam) => {
				this.transferMessages(p.message);
			}
		);

		this.subscribeOnStateChange();
	}

	private subscribeOnStateChange(): void {
		this.Pusher.connection.bind('state_change', (states: StateChangePayload) => {
			this.state = states.current;
			this.transferStatus(this.state);
		});
	}

	public sendMessage(payload: NewMessagePayload, buttonType: string | null = null) {
		if (buttonType !== 'action') {
			this.setMyAnswerInMessages(payload);
			this.setDelayMessage();
		}

		// А потом одаем его на сервер
		this.sendMessageOnServer(
			{
				...payload,
				text: btoa(unescape(encodeURIComponent(payload.text))),
				text_body: btoa(unescape(encodeURIComponent(payload.text))),
				conversation_id: this.conversationId
			},
			'client-MessageWasSended'
		);
	}

	// Рекурсивная функция, через которую пытаемся отправить на сервер
	private sendMessageOnServer(
		message: NewMessagePayload | TriggerMessagePayload,
		triggerName: string
	) {
		if (!this.isSubscribed) {
			return setTimeout(() => this.sendMessageOnServer(message, triggerName), 500);
		}

		// При таком варианте чуть дольше ожидаем прежде чем вызвать рекурсивно еще раз
		if (this.state !== 'connected') {
			return setTimeout(() => this.sendMessageOnServer(message, triggerName), 1500);
		}

		this.channelStream.trigger(triggerName, {
			message
		});
	}

	// Отправляем событие
	public sendTrigger(payload: TriggerMessagePayload) {
		this.sendMessageOnServer(payload, 'client-TriggerEventWasFired');
	}

	public get isConnected(): boolean {
		return this.channelStream?.pusher.connection.state === 'connected';
	}

	public get isSubscribed(): boolean {
		return this.channelStream?.subscribed;
	}

	private setDelayMessage(): void {
		this.transferMessages({
			type: 'action_typing'
		});
	}

	// Устанавливаем ответ в массив сообщений
	private setMyAnswerInMessages(message: Message): void {
		// Если у нас команда или геолокация, то корректное сообщение
		// с кнопки хранится в text_body
		let text;
		if (message.type === 'command' || message.type === 'location') {
			text = message.text_body;
		} else {
			text = message.text;
		}

		this.transferMessages({
			text,
			type: 'text-my'
		});

		// И в случае гео локации мы должны сами сделать ответ от бота в ввиде
		// координат + картинка
		if (message.type === 'location') {
			this.setGeoLocationMessage(message);
		}
	}

	// Когда отправляем гео локацию - то здесь устанавливаем как будто ответ с сервера пришле с картинкой

	private setGeoLocationMessage(message: Message) {
		if (!message.text) {
			return;
		}

		const coords = message.text.split(',');

		const image =
			'https://maps.googleapis.com/maps/api/staticmap?center=' +
			coords[0] +
			'&markers=color:red%7Clabel:C%7C' +
			coords[1] +
			// TODO: Не действительный ключ
			'&zoom=14&size=300x210&key=AIzaSyBLLCPvj6o-4p-oPAMbC9svd26cl3R1TCM';

		this.transferMessages({
			text: message.text,
			type: 'text-bot',
			image
		});
	}
}
